import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useJoinTournament } from '../hooks/useJoinTournament';
import GasBanner from '../components/GasBanner';
import './TarkovLobby.css';

const API             = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const REFRESH_FILLING = 5_000;
const REFRESH_ACTIVE  = 30_000;
const MAX_PLAYERS     = 20;

function formatUSDC(raw) {
  if (!raw) return '$0';
  return `$${(Number(raw) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function useCountdown(windowEnd) {
  const [label, setLabel]     = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!windowEnd) return;
    function tick() {
      const diff = windowEnd * 1000 - Date.now();
      if (diff <= 0) { setLabel('Window closed'); setExpired(true); return; }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setLabel(`${h}h ${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`);
      setExpired(false);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [windowEnd]);

  return { label, expired };
}

function TarkovCard({ tournament, walletAddress }) {
  const [mode, setMode]               = useState('idle');
  const [tarkovId, setTarkovId]       = useState('');
  const [formError, setFormError]     = useState('');
  const [successData, setSuccessData] = useState(null);
  const { label: timerLabel, expired } = useCountdown(tournament.window_end);
  const { joinTournament, state: joinState, reset: resetJoin } = useJoinTournament();

  const pct = Math.min((tournament.player_count / MAX_PLAYERS) * 100, 100);

  const stepLabel = {
    approving:      'Step 1/3: Approving USDC...',
    joining:        'Step 2/3: Joining on-chain...',
    fetching_stats: 'Step 3/3: Fetching your current stats...',
    recording:      'Step 3/3: Fetching your current stats...',
  }[joinState.step];

  async function handleJoin(e) {
    e.preventDefault();
    if (!tarkovId.trim()) { setFormError('Tarkov username is required'); return; }
    setFormError('');
    setMode('tx');
    try {
      const data = await joinTournament({
        tournament,
        gamePlayerId: tarkovId.trim(),
        gameField:    'tarkovPlayerId',
      });
      setSuccessData(data);
      setMode('success');
    } catch (err) {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to join. Try again.');
      setMode('form');
    }
  }

  return (
    <div className="tarkov-card">
      <div className="tarkov-card__game-badge">☠️ TARKOV ARENA</div>

      <div>
        <div className="tarkov-card__prize-label">Winner Takes (90%)</div>
        <div className="tarkov-card__prize">{formatUSDC(Math.floor(Number(tournament.prize_pool) * 0.9))}</div>
        <div className="tarkov-card__prize-pool-sub">Total pool: {formatUSDC(tournament.prize_pool)} · 10% platform fee</div>
      </div>

      <div className="tarkov-card__meta">
        <span className="tarkov-card__entry">Entry: {formatUSDC(tournament.entry_fee)}</span>
        {tournament.status === 'active' && (
          <span className={`tarkov-card__timer${expired ? ' tarkov-card__timer--expired' : ''}`}>
            {timerLabel}
          </span>
        )}
        {tournament.status === 'filling' && (
          <span className="tarkov-card__badge-filling">● Filling</span>
        )}
      </div>

      {tournament.status === 'active' && (
        <div className="tarkov-card__info">
          25 min window · Last Hero mode · most kills wins · instant USDC payout
        </div>
      )}

      {tournament.status === 'filling' && (
        <div className="tarkov-card__filling-banner">
          <div className="tarkov-card__filling-title">⏳ Waiting for full lobby</div>
          <div className="tarkov-card__filling-sub">
            The 25-minute kill-race starts the moment slot 20 fills. Join now and queue up.
          </div>
        </div>
      )}

      <div>
        <div className="tarkov-card__progress-row">
          <span>Players</span>
          <span>{tournament.player_count} / {MAX_PLAYERS}</span>
        </div>
        <div className="tarkov-card__bar-track">
          <div className="tarkov-card__bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {tournament.players && tournament.players.length > 0 && (
        <div className="tarkov-card__players">
          <div className="tarkov-card__players-label">Enrolled players</div>
          <div className="tarkov-card__players-list">
            {tournament.players.map((p, i) => (
              <div key={i} className="tarkov-card__player-row">
                <span className="tarkov-card__player-handle">{p.handle}</span>
                <span className="tarkov-card__player-wallet">{p.wallet}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === 'idle' && (
        <button
          className="tarkov-card__join-btn"
          onClick={() => setMode('form')}
          disabled={tournament.player_count >= MAX_PLAYERS || (tournament.status === 'active' && expired)}
        >
          {tournament.player_count >= MAX_PLAYERS ? 'Full'
            : (tournament.status === 'active' && expired) ? 'Closed'
            : 'Join'}
        </button>
      )}

      {(mode === 'form' || mode === 'tx') && (
        <form className="tarkov-card__form" onSubmit={handleJoin}>
          <div className="tarkov-card__gas-notice">
            ⛽ Need POL for gas?{' '}
            <a
              href="https://app.uniswap.org/swap?chain=polygon&inputCurrency=0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359&outputCurrency=MATIC&exactAmount=0.10&exactField=input"
              target="_blank"
              rel="noopener noreferrer"
              className="tarkov-card__gas-link"
            >
              Swap $0.10 USDC → POL →
            </a>
          </div>
          <div className="tarkov-card__stats-notice">
            ⚠️ Enter your <strong>Tarkov Arena username</strong> exactly as it appears in-game. Your profile must be <strong>public</strong> on{' '}
            <a href="https://player.tarkov.dev" target="_blank" rel="noopener noreferrer" className="tarkov-card__warning-link">
              player.tarkov.dev
            </a>{' '}
            — private profiles cannot be verified.
          </div>
          <input
            className="tarkov-card__input"
            placeholder="Tarkov Arena username"
            value={tarkovId}
            onChange={e => setTarkovId(e.target.value)}
            disabled={mode === 'tx'}
            autoFocus
          />
          <div className="tarkov-card__profile-hint">
            Check your profile is public at{' '}
            <a href="https://player.tarkov.dev" target="_blank" rel="noopener noreferrer" className="tarkov-card__warning-link">
              player.tarkov.dev →
            </a>
          </div>
          {formError && <span className="tarkov-card__form-error">{formError}</span>}
          {mode === 'tx' && stepLabel && (
            <span className="tarkov-card__form-step">{stepLabel}</span>
          )}
          <div className="tarkov-card__form-row">
            <button
              type="button"
              className="tarkov-card__cancel-btn"
              onClick={() => { setMode('idle'); setTarkovId(''); setFormError(''); resetJoin(); }}
              disabled={mode === 'tx'}
            >Cancel</button>
            <button type="submit" className="tarkov-card__submit-btn" disabled={mode === 'tx'}>
              {mode === 'tx' ? 'Processing...' : 'Confirm'}
            </button>
          </div>
        </form>
      )}

      {mode === 'success' && successData && (
        <div className="tarkov-card__success">
          <div className="tarkov-card__success-title">✅ All good, you're in!</div>
          <div className="tarkov-card__success-detail">
            {successData.tournament.status === 'active' ? (
              <>Match window is live — launch Tarkov Arena and queue Last Hero now!</>
            ) : (
              <>Waiting for tournament to start ({successData.tournament.player_count}/{successData.tournament.max_players || 20} players).{' '}
                <strong style={{ color: '#e8c878' }}>Stay ready</strong> — queue Last Hero the moment the window opens!
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TarkovLobby({ walletAddress }) {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const ref = new URLSearchParams(location.search).get('ref');
    if (ref) localStorage.setItem('referralCode', ref);
  }, [location.search]);

  const fetchTournaments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tournament/active?game=tarkov`);
      setTournaments(data.tournaments);
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load tournaments. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTournaments(); }, [fetchTournaments]);

  useEffect(() => {
    const hasFilling = tournaments.some(t => t.status === 'filling');
    const id = setInterval(fetchTournaments, hasFilling ? REFRESH_FILLING : REFRESH_ACTIVE);
    return () => clearInterval(id);
  }, [tournaments, fetchTournaments]);

  return (
    <div className="tarkov-lobby">
      <Helmet>
        <title>Tarkov Arena Tournaments — Win USDC for Most Kills | EZGAME</title>
        <meta name="description" content="Join a Tarkov Arena Last Hero tournament for $1 USDC. Most kills in a 25-minute window wins 90% of the prize pool, paid instantly on Polygon." />
        <link rel="canonical" href="https://www.ezegames.live/tarkov" />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="EZGAME" />
        <meta property="og:locale"      content="en_US" />
        <meta property="og:title"       content="Tarkov Arena Kill Tournaments — Win USDC | EZGAME" />
        <meta property="og:description" content="$1 entry. 25-minute Last Hero window. 90% prize pool to the top Tarkov Arena player — paid instantly in USDC on Polygon." />
        <meta property="og:url"         content="https://www.ezegames.live/tarkov" />
        <meta property="og:image"       content="https://www.ezegames.live/og-image.png" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:site"        content="@EZ_Egames" />
        <meta name="twitter:title"       content="Tarkov Arena Kill Tournaments — Win USDC | EZGAME" />
        <meta name="twitter:description" content="$1 entry. 25-minute Last Hero window. 90% prize pool paid instantly in USDC on Polygon." />
        <meta name="twitter:image"       content="https://www.ezegames.live/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SportsEvent',
          name: 'EZGAME Tarkov Arena Last Hero Tournament',
          description: 'Tarkov Arena Last Hero kill tournament — $1 USDC entry, 25-minute match window, 90% of the prize pool to the player with the most eliminations.',
          sport: 'Escape from Tarkov Arena',
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
          location: { '@type': 'VirtualLocation', url: 'https://www.ezegames.live/tarkov' },
          organizer: { '@type': 'Organization', name: 'EZGAME', url: 'https://www.ezegames.live' },
          offers: { '@type': 'Offer', price: '1.00', priceCurrency: 'USD', availability: 'https://schema.org/InStock', url: 'https://www.ezegames.live/tarkov' },
          url: 'https://www.ezegames.live/tarkov',
        })}</script>
      </Helmet>

      {/* Hero */}
      <div className="tarkov-lobby__hero">
        <div className="tarkov-lobby__hero-bg" />
        <div className="tarkov-lobby__hero-overlay" />
        <div className="tarkov-lobby__hero-content">
          <div className="tarkov-lobby__eyebrow">☠️ Tarkov Arena Tournaments</div>
          <h1 className="tarkov-lobby__hero-title">
            Kill. Survive. <span className="tarkov-gradient-text">Win USDC.</span>
          </h1>
          <p className="tarkov-lobby__hero-sub">
            Pay $1 USDC, rack up the most kills in Last Hero mode across a 25-minute window, get paid instantly on Polygon.
            Gas under $0.01. Pure combat.
          </p>
        </div>
      </div>

      {/* How it works strip */}
      <div className="tarkov-lobby__how-strip">
        {[
          { icon: '🦊', text: 'Connect wallet' },
          { icon: '💵', text: 'Pay $1 USDC entry' },
          { icon: '☠️', text: 'Play Last Hero — most kills wins' },
          { icon: '⚡', text: 'Instant USDC payout on Polygon' },
        ].map(s => (
          <div key={s.text} className="tarkov-lobby__how-item">
            <span className="tarkov-lobby__how-icon">{s.icon}</span>
            <span className="tarkov-lobby__how-text">{s.text}</span>
          </div>
        ))}
      </div>

      {/* Tournaments */}
      <div className="tarkov-lobby__main">
        <GasBanner />
        <div className="tarkov-lobby__warning">
          <span className="tarkov-lobby__warning-icon">⚠️</span>
          <div>
            <strong>Your Tarkov Arena profile must be public before joining.</strong>{' '}
            Private profiles = kills not verified = no payout.{' '}
            <a
              href="https://player.tarkov.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="tarkov-lobby__warning-link"
            >Check if your profile is public on player.tarkov.dev →</a>
          </div>
        </div>

        <div className="tarkov-lobby__header">
          <h2 className="tarkov-lobby__title">Active <span className="tarkov-gradient-text">Tournaments</span></h2>
          {lastRefresh && (
            <span className="tarkov-lobby__refresh">Updated {lastRefresh.toLocaleTimeString()}</span>
          )}
        </div>

        {error   && <div className="tarkov-lobby__error">{error}</div>}
        {loading && <div className="tarkov-lobby__status">Loading tournaments...</div>}

        {!loading && !error && tournaments.length === 0 && (
          <div className="tarkov-lobby__status">No active Tarkov tournaments right now. Check back soon!</div>
        )}

        {!loading && tournaments.length > 0 && (
          <div className="tarkov-lobby__grid">
            {tournaments.map(t => (
              <TarkovCard key={t.id} tournament={t} walletAddress={walletAddress} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
